// Product-file add/remove for refresh mode (KAN-1464 Task 8).
//
// Files are the sanctioned exception to the staged-write invariant: add/remove
// apply immediately, matching today's product-file semantics (plan's Global
// Constraints). There is no confirm step — the refresh banner already warns
// about impact.
//
// Extracted from UpdateProductModal.tsx:734-783 (handleUploadComplete, the
// FileUploadVectorFileModal `onUploadComplete` callback): after a file is
// uploaded (a separate, multi-step pipeline owned by useFileUpload /
// FileUploadVectorFileModal — see the module note on addProductFile below),
// the modal auto-scopes it to the current product with a single
// POST /api/vector_files/update_file_scopes call (added_project_ids:
// [productId]), then fires-and-forgets markWorkProductsNeedingRebuild. There
// is no api.ts wrapper for update_file_scopes (same situation loader.ts notes
// for /api/project/{id}), so this module calls it via fetchWithAuth directly,
// following loader.ts's precedent.
//
// Remove/unscope: UpdateProductModal.tsx has NO remove path for an
// already-scoped product file — it only ever adds. The unscope operation was
// found instead in the Files V6 page's FileScopeModal.tsx (handleSave,
// ~lines 124-171): the same update_file_scopes endpoint, but with the target
// project id placed in `removed_project_ids` instead of `added_project_ids`.
// removeProductFile below ports that shape for the single-project case.
//
// markFilesChanged wraps markWorkProductsNeedingRebuild(..., fileUpdate:
// true, ...) per the brief; the caller (the refresh-mode files modal wiring
// in NewProductScreen.tsx) is responsible for calling it ONCE per batch of
// add/remove actions (on modal close with >=1 successful change), not once
// per file — see UpdateProductModal.tsx:774-777 for the per-upload
// fire-and-forget precedent this diverges from intentionally per the brief.

import { markWorkProductsNeedingRebuild } from "@/api/api";
import fetchWithAuth from "@/api/secureFetch";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Normalized current-scope shape for a single file, derived from
 * POST /api/vector_files/get_file_scopes.
 */
interface CurrentFileScope {
    isGlobal: boolean;
    companyId: number | null;
    projectIds: number[];
    featureIds: number[];
}

/**
 * update_file_scopes (app/backend/core/services/vector_files_service.py:1748-
 * 1790) does a FULL delete-and-replace of a file's company/project/feature
 * scope rows for the given fileid — it re-adds only what's in project_ids/
 * feature_ids/is_global on the payload, and the `changes.*` field is ignored
 * server-side. So this module must never send a payload built from just the
 * one project it cares about — that would silently wipe every other
 * project/feature/global scope the file carries. Instead, fetch the file's
 * CURRENT full scope first (same call FileViewer.tsx:110 makes) and compute
 * the new full scope as current ± this one project, then send that.
 *
 * Response shape (verified against vector_files_service.get_file_scopes /
 * routes/vector_files_routes.py:231-253):
 *   { scopes: { [fileid: string]: {
 *       global: { company_id, company_name } | null,
 *       projects: [{ project_id, project_name }],
 *       features: [{ feature_id, feature_name }]
 *   } } }
 * (object keys are JSON, so fileid comes back as a string even though the
 * source column is numeric — index with String(fileId).)
 *
 * companyId is carried through here (from scope.global.company_id, null when
 * not global) because update_file_scopes requires company_id whenever
 * is_global is true (vector_files_service.py:1748-1790 raises
 * ValueError("company_id is required when is_global is True") otherwise) —
 * every add/remove call below must echo it back alongside is_global.
 */
async function fetchCurrentFileScope(token: string, fileId: number): Promise<CurrentFileScope> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_file_scopes`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids: [fileId] })
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch current file scope: ${response.statusText}`);
    }

    const data = await response.json();
    const scope = data?.scopes?.[String(fileId)];

    return {
        isGlobal: !!scope?.global,
        companyId: scope?.global?.company_id ?? null,
        projectIds: (scope?.projects ?? []).map((p: { project_id: number }) => p.project_id),
        featureIds: (scope?.features ?? []).map((f: { feature_id: number }) => f.feature_id)
    };
}

/**
 * Scope an already-uploaded file to a product (auto-scope-on-upload).
 *
 * NOTE on signature: the brief's sketch takes a raw `File`, but the actual
 * upload+extract+vectorize pipeline (chunking, SSE text-extraction progress,
 * DB save) lives entirely inside useFileUpload/FileUploadVectorFileModal —
 * it needs React state for progress reporting and cannot be ported into a
 * plain async function without duplicating (and risking drift from) that
 * proven pipeline. This module instead owns the part that's actually
 * product-refresh-specific: scoping a file the UI has already finished
 * uploading (fileId/fileName from FileUploadVectorFileModal's
 * onUploadComplete) to the refresh target product. This mirrors
 * UpdateProductModal.tsx:734-783 exactly, just decoupled from the modal's
 * React state.
 *
 * update_file_scopes is a full delete-and-replace server-side (see the
 * fetchCurrentFileScope doc comment above), so a payload built from just
 * projectId would wipe any other project/feature/global scope this fileId
 * already carries. We fetch the current scope first and UNION this
 * projectId into it, preserving everything else. If that read fails, we
 * throw rather than fall back to a projectId-only payload — a failed read
 * must never turn into a destructive write.
 *
 * NOTE: when the file is globally scoped (current.isGlobal), the backend
 * ignores project_ids entirely — update_file_scopes only writes project
 * association rows in the is_global-false branch (vector_files_service.py)
 * — so this "add" is a server-side no-op for an already-global file, by
 * design. We still send the merged project_ids for consistency with
 * removeProductFile and in case the file is later unscoped from global.
 */
export async function addProductFile(
    token: string,
    projectId: number,
    fileId: number,
    fileName?: string
): Promise<{ fileId: number; fileName: string }> {
    const current = await fetchCurrentFileScope(token, fileId);

    const alreadyScoped = current.projectIds.includes(projectId);
    const mergedProjectIds = alreadyScoped ? current.projectIds : [...current.projectIds, projectId];

    const scopePayload = {
        fileid: fileId,
        is_global: current.isGlobal,
        company_id: current.companyId,
        project_ids: mergedProjectIds,
        feature_ids: current.featureIds,
        changes: {
            added_project_ids: alreadyScoped ? [] : [projectId],
            removed_project_ids: [],
            added_feature_ids: [],
            removed_feature_ids: []
        }
    };

    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scopePayload)
    });

    if (!response.ok) {
        throw new Error(`Failed to set file scope: ${response.statusText}`);
    }

    return { fileId, fileName: fileName ?? `File ${fileId}` };
}

/**
 * Unscope a file from a product. Ported from FileScopeModal.tsx's handleSave
 * (~lines 124-171): the same update_file_scopes endpoint, this time with the
 * project id in removed_project_ids. Unlike FileScopeModal (which recomputes
 * the full added/removed diff against a multi-product picker), this is the
 * single-project removal the refresh files modal needs.
 *
 * update_file_scopes is a full delete-and-replace server-side (see the
 * fetchCurrentFileScope doc comment above), so sending project_ids: []
 * unconditionally — as an earlier version of this function did — would wipe
 * every other project/feature/global scope this fileId carries, not just
 * this product's link. We fetch the current scope first and send it back
 * MINUS this projectId, preserving everything else (other projects,
 * features, and the global flag). If that read fails, we throw rather than
 * fall back to a project_ids: [] payload — a failed read must never turn
 * into a destructive write.
 */
export async function removeProductFile(token: string, projectId: number, fileId: number): Promise<void> {
    const current = await fetchCurrentFileScope(token, fileId);

    const remainingProjectIds = current.projectIds.filter(id => id !== projectId);
    const wasScoped = remainingProjectIds.length !== current.projectIds.length;

    const scopePayload = {
        fileid: fileId,
        is_global: current.isGlobal,
        company_id: current.companyId,
        project_ids: remainingProjectIds,
        feature_ids: current.featureIds,
        changes: {
            added_project_ids: [],
            removed_project_ids: wasScoped ? [projectId] : [],
            added_feature_ids: [],
            removed_feature_ids: []
        }
    };

    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scopePayload)
    });

    if (!response.ok) {
        throw new Error(`Failed to remove file scope: ${response.statusText}`);
    }
}

/**
 * Mark this product's work products as needing a rebuild after a batch of
 * file add/remove changes. Callers must call this ONCE per batch (e.g. on
 * modal close with >=1 successful change), not once per file.
 */
export async function markFilesChanged(token: string, projectId: number): Promise<void> {
    await markWorkProductsNeedingRebuild([projectId], null, null, null, true, token);
}

/** Mark product-derived work products stale after tracker or repository links change. */
export async function markIntegrationsChanged(token: string, projectId: number): Promise<void> {
    await markWorkProductsNeedingRebuild([projectId], null, null, null, false, token);
}
